import React, { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Download, RefreshCw, Search, ShieldAlert, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import { fetchHistoryTrades, fetchHistoryStats, fetchHistoryIntegrity, exportHistoryCsvUrl, fetchStatus } from "@/lib/api";

const fmtDT = (ms) => (ms ? new Date(ms).toLocaleString("en-US", { hour12: false }) : "--");
export const fmtDur = (ms) => {
    if (ms == null) return "--";
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ${s % 60}s`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ${m % 60}m`;
    const d = Math.floor(h / 24);
    return `${d}d ${h % 24}h`;
};
const pnlColor = (v) => (v > 0 ? "#2BE38B" : v < 0 ? "#FF5C6C" : "#A7B4C6");

const inputCls = "bg-[#0B0F14] border border-[#1B2A3A] rounded px-2 py-1.5 text-xs text-[#E6EDF6] font-mono w-full focus:outline-none focus:border-[#2EE6D6]";
const labelCls = "text-[9px] uppercase tracking-[0.14em] text-[#6F7D92] mb-1 block";

const StatCard = ({ label, value, sub, color, testId }) => (
    <div className="panel px-3 py-2.5" data-testid={testId}>
        <div className="text-[9px] uppercase tracking-[0.16em] text-[#6F7D92]">{label}</div>
        <div className="text-lg font-mono mt-0.5" style={{ color: color || "#E6EDF6" }}>{value}</div>
        {sub && <div className="text-[10px] text-[#6F7D92] font-mono">{sub}</div>}
    </div>
);

const PAGE_SIZE = 50;
const EMPTY = { side: "all", mode: "all", network: "all", symbol: "", grid_id: "", price_min: "", price_max: "", date_from: "", date_to: "", pnl_min: "", search: "", include_increments: true };

const DEFAULT_API = {
    fetchHistoryTrades, fetchHistoryStats, fetchHistoryIntegrity, exportHistoryCsvUrl,
    // Used to default the Pair filter to the currently selected trading pair.
    fetchCurrentSymbol: () => fetchStatus().then((s) => s?.config?.symbol || ""),
};

export default function HistoryPage({ onBack, api, title = "Trade History & Stats", backLabel = "Dashboard" }) {
    const A = api || DEFAULT_API;
    const [filters, setFilters] = useState(EMPTY);
    const [applied, setApplied] = useState(EMPTY);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [stats, setStats] = useState(null);
    const [integrity, setIntegrity] = useState(null);
    const [loading, setLoading] = useState(false);

    const toParams = (f, forStats = false) => {
        const p = {};
        if (!forStats && f.side !== "all") p.side = f.side;
        if (f.mode !== "all") p.mode = f.mode;
        if (f.network !== "all") p.network = f.network;
        if (f.symbol) p.symbol = f.symbol;
        if (f.grid_id) p.grid_id = f.grid_id;
        if (f.search) p.search = f.search;
        if (f.date_from) p.date_from_ms = new Date(f.date_from).getTime();
        if (f.date_to) p.date_to_ms = new Date(f.date_to).getTime();
        if (!forStats) {
            if (f.price_min) p.price_min = Number(f.price_min);
            if (f.price_max) p.price_max = Number(f.price_max);
            if (f.pnl_min) p.pnl_min = Number(f.pnl_min);
            p.include_increments = f.include_increments;
        }
        return p;
    };

    const load = useCallback(async (f, pg) => {
        setLoading(true);
        try {
            const iParams = {};
            if (f.mode !== "all") iParams.mode = f.mode;
            if (f.network !== "all") iParams.network = f.network;
            if (f.symbol) iParams.symbol = f.symbol;
            const [t, s, integ] = await Promise.all([
                A.fetchHistoryTrades({ ...toParams(f), limit: PAGE_SIZE, offset: pg * PAGE_SIZE }),
                A.fetchHistoryStats(toParams(f, true)),
                A.fetchHistoryIntegrity(iParams),
            ]);
            setItems(t.items || []);
            setTotal(t.total || 0);
            setStats(s);
            setIntegrity(integ);
        } catch (e) {
            toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`);
        } finally {
            setLoading(false);
        }
    }, [A]);

    useEffect(() => { load(applied, page); }, [applied, page, load]);

    // Default the Pair filter to the currently selected trading pair (item:
    // history should show the selected pair's data, not all pairs mixed).
    const fetchCurrentSymbol = A.fetchCurrentSymbol;
    useEffect(() => {
        if (!fetchCurrentSymbol) return;
        fetchCurrentSymbol().then((sym) => {
            if (!sym) return;
            setFilters((f) => ({ ...f, symbol: sym }));
            setApplied((f) => ({ ...f, symbol: sym }));
        }).catch(() => { /* keep "all pairs" */ });
    }, [fetchCurrentSymbol]);

    const apply = () => { setPage(0); setApplied(filters); };
    const reset = () => { setFilters(EMPTY); setPage(0); setApplied(EMPTY); };
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const t = stats?.totals;
    const h = stats?.hold;
    const flaggedKeys = new Set((integrity?.flagged || []).map((f) => `${f.grid_id}|${f.filled_at_ms}|${f.price}|${f.qty}`));
    const flaggedFor = (tr) => (integrity?.flagged || []).find((f) => `${f.grid_id}|${f.filled_at_ms}|${f.price}|${f.qty}` === `${tr.grid_id}|${tr.filled_at_ms}|${Number(tr.price)}|${Number(tr.qty)}`);

    return (
        <div className="min-h-screen text-[#E6EDF6]" style={{ background: "#0B0F14" }} data-testid="history-page">
            <Toaster position="bottom-right" toastOptions={{ style: { background: "#0F1620", border: "1px solid #1B2A3A", color: "#E6EDF6" } }} />
            <div className="border-b border-[#1B2A3A]" style={{ background: "#0F1620" }}>
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <button onClick={onBack} className="btn-anim text-[#A7B4C6] hover:text-[#E6EDF6] flex items-center gap-1.5 text-xs" data-testid="history-back-button">
                            <ArrowLeft size={14} /> {backLabel}
                        </button>
                        <div className="text-sm font-semibold tracking-wide">{title}</div>
                    </div>
                    <div className="flex items-center gap-2">
                        {integrity && (
                            <span title={integrity.ok
                                ? `All ${integrity.checked_sells} sells match their real BUY fills${integrity.corrected_count ? ` · ${integrity.corrected_count} historically corrected` : ""}`
                                : `${integrity.flagged_count} sell(s) booked with a wrong buy basis — PnL off by $${integrity.pnl_overstatement}`}
                                className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md text-[10px] font-mono border"
                                style={integrity.ok
                                    ? { borderColor: "rgba(43,227,139,0.35)", color: "#2BE38B", background: "rgba(43,227,139,0.07)" }
                                    : { borderColor: "rgba(240,185,74,0.4)", color: "#F0B94A", background: "rgba(240,185,74,0.08)" }}
                                data-testid="history-integrity-badge">
                                {integrity.ok ? <ShieldCheck size={12} /> : <ShieldAlert size={12} />}
                                {integrity.ok ? "PNL INTEGRITY OK" : `${integrity.flagged_count} FLAGGED · $${integrity.pnl_overstatement}`}
                            </span>
                        )}
                        <Button variant="outline" onClick={() => load(applied, page)}
                            className="btn-anim border-[#1B2A3A] bg-transparent text-[#A7B4C6] hover:bg-[#121C28] hover:text-[#E6EDF6] gap-1.5 h-8 text-xs"
                            data-testid="history-refresh-button">
                            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                        </Button>
                        <a href={A.exportHistoryCsvUrl(toParams(applied))}
                            className="btn-anim inline-flex items-center gap-1.5 px-3 h-8 rounded-md text-xs border border-[#1B2A3A] text-[#2EE6D6] hover:bg-[#121C28]"
                            data-testid="history-export-csv-link">
                            <Download size={12} /> Export CSV
                        </a>
                    </div>
                </div>
            </div>

            <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-4 space-y-4">
                {/* Filters */}
                <div className="panel p-4" data-testid="history-filters-panel">
                    <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-11 gap-3">
                        <div>
                            <label className={labelCls}>Pair</label>
                            <input className={inputCls} value={filters.symbol} placeholder="all pairs"
                                onChange={(e) => setFilters({ ...filters, symbol: e.target.value.toUpperCase() })}
                                data-testid="history-filter-symbol" />
                        </div>
                        <div>
                            <label className={labelCls}>Side</label>
                            <select className={inputCls} value={filters.side} onChange={(e) => setFilters({ ...filters, side: e.target.value })} data-testid="history-filter-side">
                                <option value="all">All</option><option value="BUY">BUY</option><option value="SELL">SELL</option>
                            </select>
                        </div>
                        <div>
                            <label className={labelCls}>Mode</label>
                            <select className={inputCls} value={filters.mode} onChange={(e) => setFilters({ ...filters, mode: e.target.value })} data-testid="history-filter-mode">
                                <option value="all">All</option><option value="live">LIVE</option><option value="paper">PAPER</option>
                            </select>
                        </div>
                        <div>
                            <label className={labelCls}>Network</label>
                            <select className={inputCls} value={filters.network} onChange={(e) => setFilters({ ...filters, network: e.target.value })} data-testid="history-filter-network">
                                <option value="all">All</option><option value="mainnet">Mainnet</option><option value="testnet">Testnet</option>
                            </select>
                        </div>
                        <div>
                            <label className={labelCls}>Grid ID</label>
                            <input className={inputCls} value={filters.grid_id} placeholder="e.g. 76bf97" onChange={(e) => setFilters({ ...filters, grid_id: e.target.value })} data-testid="history-filter-grid-id" />
                        </div>
                        <div>
                            <label className={labelCls}>Price Min</label>
                            <input className={inputCls} type="number" value={filters.price_min} onChange={(e) => setFilters({ ...filters, price_min: e.target.value })} data-testid="history-filter-price-min" />
                        </div>
                        <div>
                            <label className={labelCls}>Price Max</label>
                            <input className={inputCls} type="number" value={filters.price_max} onChange={(e) => setFilters({ ...filters, price_max: e.target.value })} data-testid="history-filter-price-max" />
                        </div>
                        <div>
                            <label className={labelCls}>From</label>
                            <input className={inputCls} type="datetime-local" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="history-filter-date-from" />
                        </div>
                        <div>
                            <label className={labelCls}>To</label>
                            <input className={inputCls} type="datetime-local" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="history-filter-date-to" />
                        </div>
                        <div>
                            <label className={labelCls}>Min PnL ($)</label>
                            <input className={inputCls} type="number" value={filters.pnl_min} onChange={(e) => setFilters({ ...filters, pnl_min: e.target.value })} data-testid="history-filter-pnl-min" />
                        </div>
                        <div>
                            <label className={labelCls}>Search</label>
                            <input className={inputCls} value={filters.search} placeholder="grid / combo id" onChange={(e) => setFilters({ ...filters, search: e.target.value })} data-testid="history-filter-search" />
                        </div>
                    </div>
                    <div className="flex items-center gap-3 mt-3">
                        <Button onClick={apply} className="btn-anim h-8 text-xs bg-[#2EE6D6] hover:bg-[#43F0E0] text-[#0B0F14] gap-1.5" data-testid="history-apply-filters-button">
                            <Search size={12} /> Apply Filters
                        </Button>
                        <Button variant="outline" onClick={reset} className="btn-anim h-8 text-xs border-[#1B2A3A] bg-transparent text-[#A7B4C6] hover:bg-[#121C28]" data-testid="history-reset-filters-button">
                            Reset
                        </Button>
                        <label className="flex items-center gap-1.5 text-[10px] text-[#A7B4C6] cursor-pointer">
                            <input type="checkbox" checked={filters.include_increments}
                                onChange={(e) => setFilters({ ...filters, include_increments: e.target.checked })}
                                data-testid="history-include-increments-checkbox" />
                            include partial-fill slices
                        </label>
                    </div>
                </div>

                {/* Stats */}
                {t && (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="history-stats-cards">
                        <StatCard label="Trades" value={t.trades} sub={`${t.buy_fills} buys · ${t.sell_fills} sells`} testId="history-stat-trades" />
                        <StatCard label="Cycles" value={t.cycles} sub={`${t.combo_cycles} combo · ${t.manual_resolutions} manual`} testId="history-stat-cycles" />
                        <StatCard label="Win Rate" value={`${t.win_rate_pct}%`} color={t.win_rate_pct >= 50 ? "#2BE38B" : "#F0B94A"} testId="history-stat-winrate" />
                        <StatCard label="Net PnL" value={`$${t.net_pnl}`} color={pnlColor(t.net_pnl)} sub={`gross $${t.gross_pnl}`} testId="history-stat-netpnl" />
                        <StatCard label="Fees" value={`$${t.fees_usdt}`} color="#F0B94A" testId="history-stat-fees" />
                        <StatCard label="Volume" value={`$${Number(t.volume_usdt).toLocaleString()}`} testId="history-stat-volume" />
                        <StatCard label="Avg Hold" value={fmtDur(h?.avg_ms)} sub={`median ${fmtDur(h?.median_ms)}`} testId="history-stat-avghold" />
                        <StatCard label="Hold Range" value={fmtDur(h?.min_ms)} sub={`max ${fmtDur(h?.max_ms)}`} testId="history-stat-holdrange" />
                    </div>
                )}

                {/* Per grid size */}
                {stats && (
                    <div className="panel" data-testid="history-grid-size-panel">
                        <div className="px-4 py-3 border-b border-[#1B2A3A] flex items-center justify-between">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-[#A7B4C6]">Performance by Grid Size</div>
                            {stats.best_cycle && (
                                <div className="text-[10px] font-mono text-[#6F7D92]">
                                    best <span style={{ color: "#2BE38B" }}>+${Number(stats.best_cycle.pnl).toFixed(4)}</span>
                                    {" "}· worst <span style={{ color: "#FF5C6C" }}>${Number(stats.worst_cycle?.pnl || 0).toFixed(4)}</span>
                                </div>
                            )}
                        </div>
                        <table className="w-full text-xs" data-testid="history-grid-size-table">
                            <thead>
                                <tr className="text-left text-[#6F7D92] uppercase tracking-[0.12em] text-[10px]">
                                    <th className="px-3 py-2">Grid Size</th>
                                    <th className="px-3 py-2 text-right">Cycles</th>
                                    <th className="px-3 py-2 text-right">Win Rate</th>
                                    <th className="px-3 py-2 text-right">Total PnL</th>
                                    <th className="px-3 py-2 text-right">Avg PnL / Cycle</th>
                                    <th className="px-3 py-2 text-right">Avg Hold Duration</th>
                                    <th className="px-3 py-2 text-right">Avg Qty</th>
                                    <th className="px-3 py-2 text-right">Volume</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(stats.per_grid_size || []).length === 0 && (
                                    <tr><td colSpan={8} className="px-3 py-5 text-center text-[#3A4759]">No completed cycles yet.</td></tr>
                                )}
                                {(stats.per_grid_size || []).map((r) => (
                                    <tr key={r.grid_size} className="border-t border-[#13202D] hover:bg-[#121C28]" data-testid={`history-grid-size-row-${r.grid_size}`}>
                                        <td className="px-3 py-2 font-mono text-[#2EE6D6]">${r.grid_size}</td>
                                        <td className="px-3 py-2 text-right font-mono">{r.cycles}</td>
                                        <td className="px-3 py-2 text-right font-mono" style={{ color: r.win_rate_pct >= 50 ? "#2BE38B" : "#F0B94A" }}>{r.win_rate_pct}%</td>
                                        <td className="px-3 py-2 text-right font-mono" style={{ color: pnlColor(r.pnl) }}>${r.pnl}</td>
                                        <td className="px-3 py-2 text-right font-mono" style={{ color: pnlColor(r.avg_pnl) }}>${r.avg_pnl}</td>
                                        <td className="px-3 py-2 text-right font-mono text-[#A7B4C6]">{fmtDur(r.avg_hold_ms)}</td>
                                        <td className="px-3 py-2 text-right font-mono text-[#A7B4C6]">{r.avg_qty}</td>
                                        <td className="px-3 py-2 text-right font-mono text-[#A7B4C6]">${Number(r.volume_usdt).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Trades table */}
                <div className="panel" data-testid="history-trades-panel">
                    <div className="px-4 py-3 border-b border-[#1B2A3A] flex items-center justify-between">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#A7B4C6]">Trades ({total})</div>
                        <div className="flex items-center gap-2 text-[10px] font-mono text-[#6F7D92]">
                            <button disabled={page === 0} onClick={() => setPage(page - 1)}
                                className="btn-anim px-2 py-1 border border-[#1B2A3A] rounded disabled:opacity-30 hover:bg-[#121C28]"
                                data-testid="history-prev-page-button">‹ Prev</button>
                            <span data-testid="history-page-indicator">page {page + 1} / {pages}</span>
                            <button disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}
                                className="btn-anim px-2 py-1 border border-[#1B2A3A] rounded disabled:opacity-30 hover:bg-[#121C28]"
                                data-testid="history-next-page-button">Next ›</button>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs" data-testid="history-trades-table">
                            <thead>
                                <tr className="text-left text-[#6F7D92] uppercase tracking-[0.12em] text-[10px]">
                                    <th className="px-3 py-2">Filled At</th>
                                    <th className="px-3 py-2">Side</th>
                                    <th className="px-3 py-2">Grid</th>
                                    <th className="px-3 py-2 text-right">Price</th>
                                    <th className="px-3 py-2 text-right">Qty</th>
                                    <th className="px-3 py-2 text-right">Buy Basis</th>
                                    <th className="px-3 py-2 text-right">Grid Size</th>
                                    <th className="px-3 py-2 text-right">PnL</th>
                                    <th className="px-3 py-2 text-right">Fee</th>
                                    <th className="px-3 py-2">Mode</th>
                                    <th className="px-3 py-2">Tags</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.length === 0 && (
                                    <tr><td colSpan={11} className="px-3 py-6 text-center text-[#3A4759]">No trades match the filters.</td></tr>
                                )}
                                {items.map((tr, i) => {
                                    const pnl = Number(tr.realized_pnl || 0);
                                    const flag = tr.side === "SELL" && flaggedKeys.size > 0 ? flaggedFor(tr) : null;
                                    return (
                                        <tr key={`${tr.grid_id}-${tr.filled_at_ms}-${i}`} className="border-t border-[#13202D] hover:bg-[#121C28]" data-testid="history-trade-row">
                                            <td className="px-3 py-2 font-mono text-[#A7B4C6]">{fmtDT(tr.filled_at_ms)}</td>
                                            <td className="px-3 py-2">
                                                <span className="font-mono text-[10px]" style={{ color: tr.side === "BUY" ? "#2BE38B" : "#FF5C6C" }}>{tr.side}</span>
                                            </td>
                                            <td className="px-3 py-2 font-mono text-[#2EE6D6]">{String(tr.grid_id || "").slice(0, 8)}</td>
                                            <td className="px-3 py-2 text-right font-mono">${Number(tr.price).toFixed(2)}</td>
                                            <td className="px-3 py-2 text-right font-mono text-[#A7B4C6]">{tr.qty}</td>
                                            <td className="px-3 py-2 text-right font-mono" style={{ color: flag ? "#F0B94A" : "#6F7D92" }}>{tr.buy_price ? `$${Number(tr.buy_price).toFixed(2)}` : "--"}</td>
                                            <td className="px-3 py-2 text-right font-mono text-[#6F7D92]">{tr.grid_size ? `$${tr.grid_size}` : "--"}</td>
                                            <td className="px-3 py-2 text-right font-mono" style={{ color: tr.side === "SELL" ? pnlColor(pnl) : "#6F7D92" }}>
                                                {tr.side === "SELL" ? `${pnl >= 0 ? "+" : ""}$${pnl.toFixed(4)}` : "—"}
                                            </td>
                                            <td className="px-3 py-2 text-right font-mono text-[#6F7D92]">{tr.fee_usdt ? `$${Number(tr.fee_usdt).toFixed(4)}` : "--"}</td>
                                            <td className="px-3 py-2 font-mono text-[10px] text-[#6F7D92] uppercase">{tr.mode || "--"}</td>
                                            <td className="px-3 py-2 text-[9px] font-mono space-x-1">
                                                {flag && <span className="px-1 py-0.5 rounded" title={`Real basis $${flag.real_basis} → real PnL $${flag.real_pnl} (booked $${flag.booked_pnl})`} style={{ background: "rgba(240,185,74,0.15)", color: "#F0B94A" }} data-testid="history-basis-mismatch-tag">BASIS?</span>}
                                                {tr.pnl_corrected && <span className="px-1 py-0.5 rounded" title={`Corrected from $${Number(tr.orig_realized_pnl ?? 0).toFixed(4)} (basis $${Number(tr.orig_buy_price ?? 0).toFixed(2)})`} style={{ background: "rgba(46,230,214,0.12)", color: "#2EE6D6" }} data-testid="history-corrected-tag">CORRECTED</span>}
                                                {tr.increment && <span className="px-1 py-0.5 rounded" style={{ background: "rgba(167,180,198,0.1)", color: "#A7B4C6" }}>SLICE</span>}
                                                {tr.combo_id && <span className="px-1 py-0.5 rounded" style={{ background: "rgba(61,167,255,0.12)", color: "#3DA7FF" }}>COMBO</span>}
                                                {tr.manual_resolution && <span className="px-1 py-0.5 rounded" style={{ background: "rgba(240,185,74,0.12)", color: "#F0B94A" }}>MANUAL</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
